import React, { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import cn from 'classnames';
import Label from '../label';
import './styles/index';

type Props = {
  value?: string;
  onChange?: (e: any) => void;
  label?: string;
  className?: string;
  labelClassName?: string;
  labelId?: string;
  placeholder?: string;
  id?: string;
  error?: boolean;
  errorMessage?: string;
  cy?: string;
  description?: string;
};

const WysiwygEditor = ({
  value,
  onChange,
  label,
  className,
  labelClassName,
  labelId,
  placeholder,
  id,
  error,
  errorMessage,
  cy,
  description,
}: Props) => {
  const [editorState, setEditorState] = useState<EditorState>(() => {
    if (value) {
      const contentBlock = htmlToDraft(value);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        return EditorState.createWithContent(contentState);
      }
    }
    return EditorState.createEmpty();
  });

  // Update editor state when value prop changes externally
  useEffect(() => {
    // Get the current HTML content from the editor
    const currentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    
    // Only update if the external value is different from what we have
    // This prevents resetting the cursor position during typing
    if (currentHtml.trim() !== (value || '').trim()) {
      if (value) {
        const contentBlock = htmlToDraft(value);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          setEditorState(EditorState.createWithContent(contentState));
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [value, editorState]);

  const onEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    
    if (onChange) {
      const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
      // Create a synthetic event-like object that matches the expected interface
      const syntheticEvent = {
        target: {
          value: html,
        },
        currentTarget: {
          value: html,
        },
        nativeEvent: {} as Event,
        bubbles: false,
        cancelable: false,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: false,
        preventDefault: () => {},
        isDefaultPrevented: () => false,
        stopPropagation: () => {},
        isPropagationStopped: () => false,
        persist: () => {},
        timeStamp: Date.now(),
        type: 'change',
      };
      onChange(syntheticEvent as any);
    }
  };

  return (
    <div
      data-cy={cy || 'wysiwyg-editor'}
      className={cn({
        'editor__wysiwyg': true,
        'boone-form__field--error': error,
        [className]: Boolean(className),
      })}
    >
      <Label
        id={labelId}
        text={label}
        description={description}
        className={labelClassName}
      />
      <Editor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        wrapperClassName="wysiwyg-wrapper"
        editorClassName="wysiwyg-editor"
        toolbarClassName="wysiwyg-toolbar"
        placeholder={placeholder}
        toolbar={{
          options: ['inline', 'blockType', 'list', 'link', 'history'],
          inline: {
            inDropdown: false,
            options: ['bold', 'italic', 'underline'],
          },
          blockType: {
            inDropdown: true,
            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
          },
          list: {
            inDropdown: false,
            options: ['unordered', 'ordered'],
          },
          link: {
            inDropdown: false,
            options: ['link'],
          },
        }}
      />
      {error && errorMessage && (
        <div className="boone-form__field__input__with-message__message--error">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

WysiwygEditor.defaultProps = {
  error: false,
};

export default WysiwygEditor;
